import { createContext, FC, useContext, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as _ from "lodash";

import {
  ChartFilterType,
  ChartList,
  ChartType,
  DataDescriptorEntity,
  DataDescriptorList,
  DataDictionary,
  Enterprise,
  EnterpriseAppState,
  EnumList,
  MetricCollection,
  MetricEntity
} from "@ctra/api";

import { isDispatched, Optional } from "@ctra/utils";
import { useFarm, useFarmList } from "@farms";
import * as Sentry from "@sentry/react";
import { useDeepCompareMemo } from "use-deep-compare";

export interface ChartInfo {
  familyName: Optional<string>;
  metricName: string;
  variantType: string;
  sourceNames: Optional<string[]>;
  nameToDisplay: string;
}

interface ContextType {
  /**
   * all the data descriptors
   */
  dataDescriptors: DataDescriptorList;
  /**
   * all the metrics
   */
  metrics: MetricCollection;
  /**
   * subsets of data descriptors which are frequently used
   */
  subsets: {
    /**
     * data descriptors supported by the current farm
     */
    farm: Array<DataDescriptorEntity["id"]>;
    /**
     * data descriptors which support projections
     */
    projectionsEnabled: Array<DataDescriptorEntity["id"]>;
    /**
     * data descriptors which may render a chart for the current scope
     */
    chartScoped: Array<DataDescriptorEntity["id"]>;
  };
  groups: {
    /**
     * metrics grouped by category
     */
    byCategory: Record<string, MetricEntity[]>;
    /**
     * metrics grouped by family
     */
    byFamilyCategory: Record<string, Record<string, MetricEntity[]>>;
    /**
     * all the metric families
     */
    families: Pick<_.Dictionary<[MetricEntity, ...MetricEntity[]]>, string | number>;
    /**
     * metrics which support projections
     */
    projectionMetrics: Array<MetricEntity["id"]>;
  };
  api: {
    /**
     * Tell whether the metric has a view support
     * @param {MetricEntity} metric
     * @returns {boolean}
     */
    hasViewSupport: (metric: MetricEntity | DataDescriptorEntity, view?: ChartType[]) => boolean;
    /**
     * Extract the combination of metric/source/variant from a metric ID
     * @param {MetricEntity["id"]} variantID
     * @returns {ChartSource}
     */
    resolveMetricByVariantID: (variantID: MetricEntity["id"]) => MetricEntity;
    /**
     * Resolve chart information from the metric and the variant
     * @param {MetricEntity["id"]} metricID
     * @param {DataDescriptorEntity["id"]} variantID
     * @returns {ChartInfo}
     */
    getChartInfo: (metricID: MetricEntity["id"], variantID: DataDescriptorEntity["id"]) => ChartInfo;
    /**
     * Get the default variant for any given metric id
     * @param {MetricEntity["id"]} metricID
     * @param {{useFarmContext: boolean}} options
     * @returns {DataDescriptorEntity}
     */
    getDefaultVariant: (
      metricID: MetricEntity["id"],
      options?: { useFarmContext: boolean }
    ) => DataDescriptorEntity;
  };
  /**
   * meta information about the data dictionary
   */
  meta: {
    /**
     * all the enums
     */
    enums: EnumList;
    /**
     * whether the data dictionary is the root one (not nested in each other)
     */
    isDefault: boolean;
    /**
     * whether the data dictionary is still loading
     */
    isLoading: boolean;
  };
}

/**
 * Tell if the given metric is a MetricEntity
 * @param {MetricEntity | DataDescriptorEntity} entity
 * @returns {entity is MetricEntity}
 */
const isMetric = (entity: MetricEntity | DataDescriptorEntity): entity is MetricEntity => {
  return !!(entity as MetricEntity).variants;
};

/**
 * Make a default context for user entities
 * @type {React.Context<ContextType>}
 */
const DefaultContext = createContext<ContextType>({
  dataDescriptors: {},
  metrics: {},
  meta: { isLoading: true, isDefault: true, enums: {} },
  groups: { byCategory: {}, byFamilyCategory: {}, families: {}, projectionMetrics: [] },
  api: {
    hasViewSupport: () => true,
    resolveMetricByVariantID: () => ({} as MetricEntity),
    getDefaultVariant: () => ({} as DataDescriptorEntity),
    getChartInfo: () => ({} as ChartInfo)
  },
  subsets: {
    farm: [],
    chartScoped: [],
    projectionsEnabled: []
  }
});

/**
 * Hook to get the user info within the context
 */
export const useDataDictionary = (): ContextType => useContext(DefaultContext);

/**
 * Data dictionary provider
 * @param {React.ReactElement<any, string | React.JSXElementConstructor<any>> | string | number | {} | Iterable<React.ReactNode> | React.ReactPortal | boolean | null | undefined} children
 * @returns {JSX.Element}
 */
const _DataDictionaryProvider: FC = ({ children }) => {
  const dispatch = useDispatch();
  const { farm, site } = useFarm();
  const { farmList } = useFarmList();

  const {
    meta: { isDefault }
  } = useDataDictionary();

  if (!isDefault) {
    console.error("You should not nest DataDictionaryContext.");

    Sentry.captureException("Nested data dictionary contexts.", {
      tags: {
        diagnostics: "dataDictionaryError"
      }
    });
  }

  /**
   * Attempt to get metrics
   */
  const metrics = useSelector<EnterpriseAppState, MetricCollection>((state) =>
    Enterprise.entities.getMetricList(state, { farmID: farm?.id, siteID: site?.id })
  );

  /**
   * Get the chart list from the store
   * @type {ChartList}
   */
  const charts = useSelector<EnterpriseAppState, ChartList>((state) =>
    Enterprise.entities.getChartList(state)
  );

  /**
   * Attempt to get data descriptors
   * @type {DataDescriptorList}
   */
  const dataDictionary = useSelector<EnterpriseAppState, DataDescriptorList>((state) =>
    isDefault ? Enterprise.entities.getDataDictionary(state) : {}
  );

  /**
   * Attempt to get all enums
   * @type {DataDescriptorList}
   */
  const enums = useSelector<EnterpriseAppState, EnumList>((state) => Enterprise.entities.getEnums(state));

  /**
   * Pick only the metrics that do have "farm" variants if no farm is selected. Omit the rest.
   */
  const allFarmMetrics = _.filter(metrics, ({ variants }) =>
    _.some(variants, (variant) =>
      _.isEqual(_.get(dataDictionary, [variant, "dataProperties", "type"]), "farm")
    )
  );

  /**
   * Group metrics by category
   */
  const categoryGroup: Record<string, MetricEntity[]> = _.groupBy(
    (farm ? metrics : allFarmMetrics) as MetricCollection,
    "category"
  );

  /**
   * Add a layer of family group
   * if the group does not exist, group it by name for the sake of level consistency
   */
  const categoryFamilyGroup: Record<string, Record<string, MetricEntity[]>> = _.mapValues(
    categoryGroup,
    (metric) => _.groupBy(metric, ({ family, id }) => family || id)
  );

  /**
   * Get only a list of families
   */
  const metricFamilies = _.omit(
    _.groupBy(metrics, ({ family }) => family),
    "undefined"
  );

  /**
   * Get the farm subset (descriptors supported by the current farm and site)
   * @type {Array<DataDescriptorEntity["id"]>}
   */
  const farmSubset = useSelector<EnterpriseAppState, Array<DataDescriptorEntity["id"]>>((state) => {
    if (!(farm?.id && isDefault)) {
      return [];
    }

    const variants = Enterprise.entities.getDataDictionary(state, {
      filter: {
        supportedFarms: [farm.id]
      }
    });

    const isFarmType = _.pickBy(variants, ({ dataProperties }) => {
      return dataProperties.type === "farm";
    });

    const withSiteSupport = _.pickBy(variants, ({ dataProperties, filters }) => {
      return _.has(filters, ChartFilterType.siteId);
    });

    return _.map(site?.id ? withSiteSupport : isFarmType, "id");
  });

  /**
   * Get the data descriptors which support projections
   * @type {Array<DataDescriptorList[keyof DataDescriptorList]>}
   */
  const projectionEnabledSubset = useDeepCompareMemo(() => {
    return _.map(
      _.filter(
        dataDictionary,
        ({ projectionEnabledFarms, valueProperties: { maxProjectionInterval } }) =>
          !!maxProjectionInterval && farm?.id
            ? _.includes(projectionEnabledFarms, farm.id)
            : _.size(projectionEnabledFarms) > 0 // _.isEqual(_.sortBy(projectionEnabledFarms), _.sortBy(_.map(farmList, "id")))
      ),
      "id"
    );
  }, [farm, farmList]);

  /**
   * Get the metrics which support projections
   * @returns metric id or family name
   */
  const projectionMetrics = _.chain(metrics)
    .values()
    .filter((metric) => _.some(metric.variants, (variant) => _.includes(projectionEnabledSubset, variant)))
    .map(({ family, id }) => _.defaultTo(family, id))
    .value();

  /**
   * Count the number of metrics (which can indicate an update)
   * @type {number}
   */
  const metricCount = _.size(dataDictionary);

  /**
   * Get the data descriptors which may render a chart for the current scope.
   * Eg. filter out when a farm is picked but not supported or all farms are picked but the chart supports a single farm
   * @type {Array<DataDescriptorEntity["id"]>}
   */
  const chartSubset = useMemo(
    () =>
      isDefault
        ? _.map(
            _.pickBy(dataDictionary, (dataDescriptor) => {
              const { supportedFarms, supportedCharts, filters } = dataDescriptor;
              const [mainChartID] = supportedCharts;

              const {
                flags: { isMultiFarmFilterSupported }
              } = charts[mainChartID];

              /**
               * Tell if the selected farm is supported by the data descriptor,
               * or whether the chart supports multiple farms
               */
              const supportsCurrentFarmView = farm?.id
                ? site?.id
                  ? supportedFarms.includes(farm.id) && _.has(filters, ChartFilterType.siteId)
                  : supportedFarms.includes(farm.id)
                : isMultiFarmFilterSupported;

              return supportsCurrentFarmView;
            }),
            "id"
          )
        : [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [metricCount, farm, site, isDefault]
  );

  /**
   * Tell whether the fetching action has been dispatched
   * @type {boolean}
   */
  const dispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, DataDictionary.types.FETCH_METRICS)
  );

  /**
   * Tell whether the fetching action has been dispatched for enums
   * @type {boolean}
   */
  const enumsActionDispatched = useSelector<EnterpriseAppState, boolean>((state) =>
    isDispatched(state, DataDictionary.types.FETCH_ENUMS)
  );

  /**
   * Fetch the data dictionary if it is not yet present in the store
   */
  useEffect(() => {
    if (isDefault && _.isEmpty(dataDictionary) && !dispatched) {
      dispatch(DataDictionary.actions.fetchMetrics.start());
    }
  }, [dispatch, isDefault, dispatched, dataDictionary]);

  /**
   * Fetch the enums if not yet present in the store
   */
  useEffect(() => {
    if (_.isEmpty(enums) && !enumsActionDispatched) {
      dispatch(DataDictionary.actions.fetchEnums.start());
    }
  }, [dispatch, enums, enumsActionDispatched]);

  /**
   * Reverse lookup a metric by a variant ID
   * @param {DataDescriptorEntity["id"]} variantID
   * @returns {MetricEntity}
   */
  const resolveMetricByVariantID = (variantID: DataDescriptorEntity["id"]): MetricEntity => {
    const metric = _.find(metrics, ({ variants }) => _.includes(variants, variantID));

    if (!metric) {
      throw new Error(`No metric found for variant ID: ${variantID}`);
    }

    return metric;
  };

  /**
   * Get the default variant for any given metric id
   * @param {MetricEntity["id"]} metricID
   * @returns {DataDescriptorEntity}
   */
  const getDefaultVariant = (metricID: MetricEntity["id"]): DataDescriptorEntity => {
    const metric = metrics[metricID];

    if (!metric) {
      throw new Error(`No metric found for ID: ${metricID}`);
    }

    const { variants } = metric;

    const withFarmSupport = _.filter(variants, (variant) => {
      const { supportedFarms } = dataDictionary[variant] || {};

      return farm?.id ? _.includes(supportedFarms, farm.id) : supportedFarms.length > 0;
    });

    /**
     * Get the farm type variant
     * @type {DataDescriptorEntity["id"] | undefined}
     */
    const farmVariant = _.find(withFarmSupport, (variant) =>
      _.isEqual(_.get(dataDictionary, [variant, "dataProperties", "type"]), "farm")
    );

    const siteVariant = _.find(withFarmSupport, (variant) =>
      _.has(dataDictionary, [variant, "filters", ChartFilterType.siteId])
    );

    const variantID = _.defaultTo(site?.id ? siteVariant : farmVariant, _.first(withFarmSupport));

    if (!variantID || !dataDictionary[variantID]) {
      throw new Error(`No default variant found for metric ID: ${metricID}`);
    }

    return dataDictionary[variantID];
  };

  /**
   * Get the chart information from a metric and a variant
   * @param {MetricEntity["id"]} metricID
   * @param {DataDescriptorEntity["id"]} variantID
   * @returns {ChartInfo}
   * @todo rename to something metric/variant related
   */
  const getChartInfo = (metricID: MetricEntity["id"], variantID: DataDescriptorEntity["id"]): ChartInfo => {
    const metric = metrics[metricID];

    if (!metric) {
      console.error("No metric found for ID", metricID, "in", metrics);
      throw new Error(`No metric found for ID: ${metricID}`);
    }

    const { family, name, sourceTypes } = metric;

    const variant = dataDictionary[variantID];

    if (!variant) {
      console.error("No variant found for ID", variantID, "in", dataDictionary);
      throw new Error(`No variant found for ID: ${variantID}`);
    }

    const {
      dataProperties: { type: variantType }
    } = variant;

    return {
      familyName: family,
      metricName: name,
      variantType,
      sourceNames: sourceTypes,
      nameToDisplay: family || name
    };
  };

  /**
   * Tell whether a metric/variant supports the given views
   * @param {MetricEntity} entity
   * @param {ChartType[]} view
   * @returns {boolean}
   */
  const hasViewSupport = (entity: MetricEntity | DataDescriptorEntity, view?: ChartType[]): boolean => {
    if (!view || !_.size(view)) {
      return true; // If no view is specified, assume all views are supported
    }

    if (isMetric(entity)) {
      const { variants: variantIDList } = entity;
      const variants = _.pick(dataDictionary, variantIDList);

      /**
       * Pick the views from the chart ids
       * @type {ChartType[]}
       */
      const supportedViews = _.intersection(
        ..._.map(variants, ({ supportedCharts }) => {
          return _.map(supportedCharts, (chartID) => {
            const [, view] = chartID.split("-");

            return view as ChartType;
          });
        })
      );

      return _.some(supportedViews, (supportedView) => _.includes(view, supportedView));
    } else {
      const { supportedCharts } = entity;

      const views = _.map(supportedCharts, (chartID) => {
        const [, view] = chartID.split("-");

        return view as ChartType;
      });

      return _.some(views, (supportedView) => _.includes(view, supportedView));
    }
  };

  return isDefault ? (
    <DefaultContext.Provider
      value={{
        dataDescriptors: dataDictionary,
        metrics,
        meta: {
          isDefault: false,
          isLoading: _.isEmpty(dataDictionary),
          enums
        },
        subsets: {
          projectionsEnabled: projectionEnabledSubset,
          chartScoped: chartSubset,
          farm: farmSubset
        },
        groups: {
          byCategory: categoryGroup,
          byFamilyCategory: categoryFamilyGroup,
          families: metricFamilies,
          projectionMetrics
        },
        api: {
          hasViewSupport,
          resolveMetricByVariantID,
          getDefaultVariant,
          getChartInfo
        }
      }}
    >
      {children}
    </DefaultContext.Provider>
  ) : (
    <>{children}</>
  );
};

/**
 * Context for the data dictionary
 * @type {{Consumer: React.ExoticComponent<React.ConsumerProps<ContextType>>, Provider: React.FunctionComponent<{}>}}
 */
export const DataDictionaryContext = {
  Consumer: DefaultContext.Consumer,
  Provider: _DataDictionaryProvider
};
